import React, { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, LinkedinLogo, MapPin, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../lib/motion";
import { Eyebrow } from "../components/ui-kit";

const STAGES = ["Idea / Validation", "Pre-seed", "Seed", "Early Revenue", "Scaling", "Established Business"];

const initial = { name: "", email: "", company: "", website: "", stage: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm(initial);
      toast.success("Thank you — your message is on its way.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[15px] text-slate-ink outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-navy";

  return (
    <div data-testid="contact-page">
      <section className="bg-cloud pt-40 pb-16 lg:pt-48 lg:pb-20">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-7 max-w-3xl font-heading text-5xl font-light leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-[4.2rem]">
              Let's Build <span className="font-semibold text-gold">Together.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-600">
              Whether you're validating your first idea or preparing for your next
              stage of growth, we'd love to hear your story.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white pb-28 lg:pb-36">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-[1.4fr_0.9fr]">
            {/* Form */}
            <Reveal className="rounded-[28px] border border-slate-100 bg-white p-8 shadow-[0_20px_60px_rgba(11,31,58,0.06)] lg:p-12">
              {done ? (
                <div
                  data-testid="contact-success"
                  className="flex min-h-[420px] flex-col items-center justify-center text-center"
                >
                  <CheckCircle size={64} weight="fill" className="text-teal" />
                  <h2 className="mt-6 font-heading text-3xl font-semibold text-navy">
                    Message received.
                  </h2>
                  <p className="mt-3 max-w-sm text-slate-600">
                    Thank you for reaching out. Nirmalya will be in touch personally to
                    start the conversation.
                  </p>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => setDone(false)}
                    className="mt-8 text-sm font-semibold text-navy underline underline-offset-4 transition-colors hover:text-gold"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-navy">Name *</label>
                      <input
                        data-testid="contact-name"
                        className={inputCls}
                        placeholder="Your full name"
                        value={form.name}
                        onChange={update("name")}
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-navy">Email *</label>
                      <input
                        data-testid="contact-email"
                        type="email"
                        className={inputCls}
                        placeholder="you@company.com"
                        value={form.email}
                        onChange={update("email")}
                      />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-navy">Company</label>
                      <input
                        data-testid="contact-company"
                        className={inputCls}
                        placeholder="Company name"
                        value={form.company}
                        onChange={update("company")}
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-navy">Website</label>
                      <input
                        data-testid="contact-website"
                        className={inputCls}
                        placeholder="https://"
                        value={form.website}
                        onChange={update("website")}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-navy">Current Stage</label>
                    <Select
                      value={form.stage}
                      onValueChange={(v) => setForm((f) => ({ ...f, stage: v }))}
                    >
                      <SelectTrigger data-testid="contact-stage" className={`${inputCls} h-auto`}>
                        <SelectValue placeholder="Where are you today?" />
                      </SelectTrigger>
                      <SelectContent>
                        {STAGES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`stage-option-${s}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-navy">Message *</label>
                    <textarea
                      data-testid="contact-message"
                      rows={5}
                      className={`${inputCls} resize-none`}
                      placeholder="Tell us about what you're building..."
                      value={form.message}
                      onChange={update("message")}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-7 py-4 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-navy-950 disabled:opacity-60 sm:w-auto"
                  >
                    {submitting ? "Sending..." : "Send Message"}
                    {!submitting && <PaperPlaneTilt size={17} weight="bold" />}
                  </button>
                </form>
              )}
            </Reveal>

            {/* Sidebar */}
            <Reveal delay={0.1} className="lg:pt-4">
              <div className="rounded-[28px] bg-navy p-10 text-white">
                <h3 className="font-heading text-2xl font-semibold">Reach us directly</h3>
                <p className="mt-3 text-white/60">
                  Prefer a direct line? We read every message personally.
                </p>
                <div className="mt-8 space-y-6">
                  <a
                    href="mailto:foun.plus@gmail.com"
                    data-testid="contact-sidebar-email"
                    className="flex items-start gap-4 transition-colors duration-300 hover:text-gold"
                  >
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/10 text-gold">
                      <EnvelopeSimple size={20} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.2em] text-white/50">Email</span>
                      <span className="mt-1 block font-medium">foun.plus@gmail.com</span>
                    </span>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/founder-plus/"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-sidebar-linkedin"
                    className="flex items-start gap-4 transition-colors duration-300 hover:text-gold"
                  >
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/10 text-gold">
                      <LinkedinLogo size={20} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.2em] text-white/50">LinkedIn</span>
                      <span className="mt-1 block font-medium">Founder+</span>
                    </span>
                  </a>
                  <div className="flex items-start gap-4">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/10 text-gold">
                      <MapPin size={20} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.2em] text-white/50">Location</span>
                      <span className="mt-1 block font-medium">India · Working with founders worldwide</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-6 overflow-hidden rounded-[28px]">
                <img src="/images/contact.png" alt="A calm modern office" className="aspect-[4/3] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
